import { useMutation } from '@tanstack/react-query';
import { Download, KeyRound, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { PageHeader } from '../../components/ui/PageHeader';
import { apiClient } from '../../lib/api-client';
import { LEGAL_INFO } from '../legal/legal-info';

import { useAuth } from './auth-context';

const ROLE_LABELS: Record<string, string> = {
  SUPER_ADMIN: 'Super-administrateur',
  ADWEB_OPERATOR: 'Opérateur Adweb-Systèmes',
  CLIENT_ADMIN: 'Administrateur du client',
  CLIENT_USER: 'Utilisateur du client',
};

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-1 text-sm text-slate-900">{value}</div>
    </div>
  );
}

export function MyDataPage() {
  const { user } = useAuth();
  const [resetSent, setResetSent] = useState(false);

  const downloadExport = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('Non authentifié');
      const res = await apiClient.get(`/organizations/${user.organizationId}/export`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `adweb-mes-donnees-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    },
  });

  const resetPassword = useMutation({
    mutationFn: async () => {
      if (!user) return;
      try {
        await apiClient.post('/auth/password/forgot', { email: user.email });
      } catch {
        // L'API répond toujours 200 (anti-énumération) ; on reste neutre.
      }
    },
    onSuccess: () => {
      setResetSent(true);
      window.setTimeout(() => setResetSent(false), 8000);
    },
  });

  if (!user) return null;

  const canExport =
    user.role === 'SUPER_ADMIN' || user.role === 'ADWEB_OPERATOR' || user.role === 'CLIENT_ADMIN';

  return (
    <>
      <PageHeader
        helpTopic="donnees"
        title="Mes données"
        subtitle="Aperçu de vos informations et exercice de vos droits RGPD."
      />

      <section className="grid grid-cols-1 gap-6 px-4 sm:px-6 lg:px-8 py-6 lg:grid-cols-2">
        <div className="surface p-5">
          <h2 className="mb-4 text-sm font-medium text-slate-700">Mon compte</h2>
          <div className="grid grid-cols-2 gap-4">
            <Field label="E-mail" value={user.email} />
            <Field label="Nom" value={user.fullName} />
            <Field label="Rôle" value={ROLE_LABELS[user.role] ?? user.role} />
            <Field
              label="Authentification à deux facteurs"
              value={
                user.twoFactorEnabled ? (
                  <span className="inline-flex items-center gap-1 text-emerald-700">
                    <ShieldCheck className="h-4 w-4" aria-hidden="true" /> Activée
                  </span>
                ) : (
                  <span className="text-slate-500">Désactivée</span>
                )
              }
            />
            <Field
              label="Identifiant utilisateur"
              value={<code className="font-mono text-xs">{user.id}</code>}
            />
            <Field
              label="Identifiant organisation"
              value={<code className="font-mono text-xs">{user.organizationId}</code>}
            />
          </div>

          <div className="mt-6 space-y-2">
            {resetSent ? (
              <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
                Si un compte existe pour cette adresse, un e-mail de réinitialisation vient d'être
                envoyé. Le lien est valable 1 heure.
              </p>
            ) : (
              <button
                type="button"
                onClick={() => resetPassword.mutate()}
                disabled={resetPassword.isPending}
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
              >
                <KeyRound className="h-4 w-4" aria-hidden="true" />
                Réinitialiser mon mot de passe
              </button>
            )}
          </div>
        </div>

        <div className="rounded-xl border border-blue-100 bg-blue-50/40 p-5">
          <h2 className="mb-1 text-sm font-medium text-slate-700">
            Mes droits sur mes données (RGPD)
          </h2>
          <p className="mb-4 text-xs text-slate-500">
            Vous disposez de droits sur vos données personnelles : accès, rectification, effacement,
            portabilité, limitation, opposition. Voir la{' '}
            <Link to="/legal/confidentialite" className="text-brand hover:underline">
              Politique de confidentialité
            </Link>{' '}
            pour le détail.
          </p>

          {canExport && (
            <div className="mb-4 rounded-lg border border-slate-200 bg-white p-3">
              <div className="mb-1 text-sm font-medium text-slate-700">
                Télécharger un export de l'organisation
              </div>
              <p className="mb-3 text-xs text-slate-500">
                Récupère un fichier JSON contenant toutes les données de votre organisation
                (comptes, automatisations, exécutions, incidents, journaux d'audit), hors secrets
                techniques.
              </p>
              <button
                type="button"
                onClick={() => downloadExport.mutate()}
                disabled={downloadExport.isPending}
                className="inline-flex items-center gap-2 rounded-lg bg-brand px-3 py-1.5 text-xs font-medium text-white shadow-sm transition hover:bg-brand-700 disabled:opacity-50"
              >
                <Download className="h-3.5 w-3.5" aria-hidden="true" />
                {downloadExport.isPending ? 'Préparation…' : 'Télécharger mes données'}
              </button>
            </div>
          )}

          <div className="rounded-lg border border-slate-200 bg-white p-3 text-xs text-slate-600">
            <p className="mb-1 font-medium text-slate-700">Exercer un autre droit ?</p>
            <p>
              Pour toute demande relative à vos droits (rectification, suppression de votre compte,
              opposition, limitation, portabilité personnalisée), contactez-nous :
            </p>
            <a
              href={`mailto:${LEGAL_INFO.dpoEmail}`}
              className="mt-2 inline-block font-medium text-brand hover:underline"
            >
              {LEGAL_INFO.dpoEmail}
            </a>
            <p className="mt-2">
              Une réponse vous est apportée dans le délai maximum d'un mois prévu par le RGPD.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
